import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuthContext } from '../contexts/AuthContext';

import type { Period } from '../types/programmation';

type ProgrammationData = {
  id: string;
  title: string;
  description: string;
  startDate: Date;
  endDate: Date;
  category: string;
  userId: string;
  periods: Period[];
  createdAt: Date;
  updatedAt: Date;
  isPublic?: boolean;
};

export const useProgrammation = (programmationId?: string) => {
  const { user } = useAuthContext();
  const currentUser = user;
  const navigate = useNavigate();
  const [programmation, setProgrammation] = useState<ProgrammationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchProgrammation = async (id: string) => {
    try {
      setLoading(true);
      const programmationRef = doc(db, 'programmations', id);
      const programmationDoc = await getDoc(programmationRef);

      if (!programmationDoc.exists()) {
        throw new Error('Programmation non trouvée');
      }

      const data = programmationDoc.data();
      
      // Vérifier accès : propriétaire ou programmation publique
      const isPublic = data.isPublic === true;
      const isOwner = !!currentUser && currentUser.uid === data.userId;
      if (!isPublic) {
        // Si non public, il faut être propriétaire. Si on ne connaît pas encore l'utilisateur, on attend.
        if (!currentUser) {
          // Attendre prochaine exécution useEffect lorsque currentUser sera disponible
          return;
        }
        if (!isOwner) {
          throw new Error('Non autorisé');
        }
      }

      setProgrammation({
        id: programmationDoc.id,
        title: data.title,
        description: data.description || '',
        startDate: data.startDate?.toDate(),
        endDate: data.endDate?.toDate(),
        category: data.category || 'senior',
        userId: data.userId,
        periods: data.periods || [],
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      isPublic: data.isPublic ?? false,
      });
    } catch (err: any) {
      console.error('Erreur lors du chargement de la programmation:', err);
      setError(err.message || 'Une erreur est survenue');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const updateProgrammation = async (id: string, data: Partial<ProgrammationData>) => {
    if (!currentUser) return null;

    try {
      setLoading(true);
      const programmationRef = doc(db, 'programmations', id);
      
      const updateData = {
        ...data,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(programmationRef, updateData);
      return true;
    } catch (err) {
      console.error('Erreur lors de la mise à jour de la programmation:', err);
      setError('Une erreur est survenue lors de la mise à jour');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const deleteProgrammation = async (id: string) => {
    if (!user) return null;

    try {
      setLoading(true);
      const programmationRef = doc(db, 'programmations', id);
      
      // Vérifier que l'utilisateur est bien le propriétaire
      const programmationDoc = await getDoc(programmationRef);
      if (!programmationDoc.exists()) {
        throw new Error('Planification non trouvée');
      }
      
      const data = programmationDoc.data();
      if (data.userId !== user.uid) {
        throw new Error('Non autorisé à supprimer cette planification');
      }

      await deleteDoc(programmationRef);
      return true;
    } catch (err: any) {
      console.error('Erreur lors de la suppression de la planification:', err);
      setError(err.message || 'Une erreur est survenue lors de la suppression');
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (programmationId) {
      fetchProgrammation(programmationId);
    }
  }, [programmationId, user]);

  return {
    programmation,
    loading,
    error,
    updateProgrammation,
    deleteProgrammation,
  };
};
